import { useState } from "react";
import { Collapse } from "react-collapse";

import AlertTriangleIcon from "@assets/icons/alert-icon.svg";
import ChevronDownIcon from "@assets/icons/chevron-down.svg";

import { cn } from "@utils/tailwind.util";
import { IEmailAttachment } from "@interfaces/email.interface";
import { isAttachmentPending, isAttachmentFailed, isAttachmentCompleted } from "@utils/attachment.util";
import { Radio } from "@ui/FormElements";
import { Spinner } from "@ui/Loaders";

interface IUploaderAttachmentsListProps {
  attachments: IEmailAttachment[];
  selectedAttachmentId: string;
  onSelectAttachment: (attachment: IEmailAttachment) => void;
}

export function UploaderAttachmentsList({
  attachments,
  selectedAttachmentId,
  onSelectAttachment,
}: IUploaderAttachmentsListProps) {
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const attachmentsCounter = attachments?.length || 0;

  const handleToggleAttachments = () => setIsAttachmentsOpen((prev) => !prev);

  const getDocumentNameWithExtension = (name: string, format?: string): string => {
    if (!format) return name;

    const extension = `.${format.toLowerCase()}`;

    // if name already ends with this extension — don't add
    if (name.toLowerCase().endsWith(extension)) {
      return name;
    }

    return name + extension;
  };

  return (
    <div className="px-2 flex flex-col gap-1 font-[500] text-xs text-content-secondary dark:text-invertedPrimary80">
      <p>
        You have{" "}
        <strong className="font-[500] text-content-primary dark:text-invertedPrimary">
          {attachmentsCounter} {attachmentsCounter > 1 ? "attachments" : "attachment"}
        </strong>{" "}
        on this thread.
      </p>
      <p
        className="flex items-center gap-1 text-teal-600  dark:text-teal-200 cursor-pointer"
        onClick={handleToggleAttachments}
      >
        <u className="underline cursor-pointer">Select one to create details</u>
        <ChevronDownIcon
          width={14}
          height={14}
          className={cn("-rotate-90 text-content-secondary dark:text-invertedPrimary80", {
            "rotate-0": isAttachmentsOpen,
          })}
        />
      </p>
      <Collapse isOpened={isAttachmentsOpen}>
        <div className="pl-2 py-2 min-w-0 flex flex-col gap-2 border-l dark:border-gray-600">
          {attachments.map((attachment) => {
            const isPending = isAttachmentPending(attachment);
            const isFailed = isAttachmentFailed(attachment);
            const isReady = isAttachmentCompleted(attachment);
            const documentId = attachment?.document?.id || attachment.name || "Unknown";
            const documentName = attachment?.document?.name || attachment.name || "Unknown";
            const documentExtension = attachment.format;

            return (
              <Radio
                key={documentId}
                id={documentId}
                label={
                  <div className="min-w-0 w-full flex items-center justify-between gap-2">
                    <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                      {getDocumentNameWithExtension(documentName, documentExtension)}
                    </span>
                    {isPending && <Spinner size={15} thickness={2} />}
                    {isFailed && (
                      <AlertTriangleIcon
                        width={16}
                        height={16}
                        className="flex-shrink-0 text-red-600"
                      />
                    )}
                  </div>
                }
                value={documentId}
                isChecked={selectedAttachmentId === documentId}
                onChange={() => onSelectAttachment(attachment)}
                disabled={!isReady}
                labelClass="min-w-0 w-full text-content-primary dark:text-white text-sm font-[400]"
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
}
